import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';

import { CircularProgress } from '@/components/custom-ui/circular-progress';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { InitialScreen } from '@/pages/ItemsContainer/ItemsCompanyReport/components/IntialScreen';
import LoadingBox from '@/UI/LoadingBox';

import { GetCompaniesListApi } from './api/CompanyItems.api';
import type {
  ICompanyItemGroupedInterface,
  ICompanyItemInterface,
  ICompanyList,
} from './api/CompanyItems.interface';
import ClassifiedItems from './components/ClassifiedItems';
import FilterBox from './components/FilterItems';
import ItemsTable from './components/itemTable/ItemTable';
import ReportHeader from './components/selectCompanyForm';

function CompanyItemsReportPage() {
  // Get all companies query to use it in the form dialog
  const allCompanies = useQuery({
    queryFn: () => GetCompaniesListApi(),
    queryKey: ['all-companies'],
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [allData, setAllData] = useState<ICompanyItemInterface[] | null>(null);
  const [groupedData, setGroupedData] = useState<
    ICompanyItemGroupedInterface[] | null
  >(null);
  const [filterData, setFilterData] = useState<ICompanyItemInterface[] | null>(
    null,
  );
  const [selectedCompany, setSelectedCompany] = useState<ICompanyList | null>(
    null,
  );
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isFilterCollapsed, setIsFilterCollapsed] = useState(false);

  const selectedCompanyName = useMemo(
    () => selectedCompany?.Name || '—',
    [selectedCompany],
  );
  const periodLabel = useMemo(() => {
    if (!startDate || !endDate) return '—';
    try {
      const s = new Date(startDate).toLocaleDateString('ar-EG');
      const e = new Date(endDate).toLocaleDateString('ar-EG');
      return `${s} → ${e}`;
    } catch {
      return '—';
    }
  }, [startDate, endDate]);

  if (allCompanies?.isLoading)
    return <CircularProgress size="lg" aria-label="loading" />;

  return (
    <>
      <Helmet>
        <title> تقرير حركة اصناف الشركات </title>
      </Helmet>
      <div className="m-0 flex w-full flex-col gap-3 overflow-x-hidden p-0">
        <ReportHeader
          allCompanies={allCompanies?.data?.data?.companies}
          loading={loading}
          setAllData={setAllData}
          setGroupedData={setGroupedData}
          setFilterData={setFilterData}
          setLoading={setLoading}
          setSelectedCompany={setSelectedCompany}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
        />

        {/* Enhanced Status bar */}
        <div className="mx-2">
          <Card className="relative overflow-hidden border-0 bg-gradient-to-r from-indigo-50/90 via-white/80 to-emerald-50/90 p-3 shadow-lg backdrop-blur-sm dark:from-indigo-950/40 dark:via-slate-900/60 dark:to-emerald-950/40">
            <div className="bg-grid-pattern absolute inset-0 opacity-5" />
            <div
              className="relative flex flex-wrap items-center justify-between gap-3"
              dir="rtl"
            >
              <div className="flex flex-wrap items-center gap-2">
                <Badge className="bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-sm transition-shadow hover:shadow-md">
                  <span className="font-medium">الشركة:</span>
                  <span className="mr-1 font-bold">{selectedCompanyName}</span>
                </Badge>
                <Badge className="bg-gradient-to-r from-emerald-600 to-emerald-500 text-white shadow-sm transition-shadow hover:shadow-md">
                  <span className="font-medium">الفترة:</span>
                  <span className="mr-1">{periodLabel}</span>
                </Badge>
                {filterData && (
                  <Badge className="bg-gradient-to-r from-violet-600 to-violet-500 text-white shadow-sm transition-shadow hover:shadow-md">
                    <span className="font-medium">الأصناف:</span>
                    <span className="mr-1 font-bold">{filterData.length}</span>
                  </Badge>
                )}
              </div>

              <div className="flex items-center gap-3">
                {allData && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsFilterCollapsed(!isFilterCollapsed)}
                    className="flex items-center gap-1.5 bg-white/80 transition-all hover:bg-white dark:bg-slate-800/80 dark:hover:bg-slate-800"
                  >
                    {isFilterCollapsed ? (
                      <>
                        <PanelLeftOpen className="h-4 w-4" />
                        <span className="text-xs">عرض الفلاتر</span>
                      </>
                    ) : (
                      <>
                        <PanelLeftClose className="h-4 w-4" />
                        <span className="text-xs">إخفاء الفلاتر</span>
                      </>
                    )}
                  </Button>
                )}

                {loading && (
                  <div className="flex items-center gap-2 text-sm text-indigo-600 dark:text-indigo-300">
                    <CircularProgress size="sm" aria-label="loading" />
                    <span className="font-medium">جارٍ تحميل التقرير...</span>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>

        <div className="mx-2">
          {allData && (
            <div
              className={cn(
                'grid gap-4 transition-all duration-300',
                isFilterCollapsed
                  ? 'grid-cols-1'
                  : 'grid-cols-1 lg:grid-cols-[380px_1fr] xl:grid-cols-[400px_1fr] 2xl:grid-cols-[420px_1fr]',
              )}
            >
              {/* Enhanced Filter Panel */}
              <div
                className={cn(
                  'transform-gpu transition-all duration-300',
                  isFilterCollapsed && 'hidden',
                )}
              >
                <div className="sticky top-2">
                  <Card className="border-0 bg-gradient-to-br from-white/95 to-gray-50/90 shadow-2xl backdrop-blur-md dark:from-slate-900/95 dark:to-slate-800/90">
                    <div className="relative">
                      <div className="absolute -right-2 -top-2 h-20 w-20 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 blur-2xl" />
                      <div className="absolute -bottom-2 -left-2 h-24 w-24 rounded-full bg-gradient-to-tr from-emerald-500/20 to-emerald-500/5 blur-2xl" />
                      <div className="relative p-4">
                        <FilterBox
                          allData={allData}
                          filterData={filterData}
                          setFilterData={setFilterData}
                          groupedData={groupedData}
                          selectedCompany={selectedCompany}
                          startDate={startDate}
                          endDate={endDate}
                        />
                      </div>
                    </div>
                  </Card>
                </div>
              </div>

              {/* Main Content Area */}
              <div className="grid w-full grid-cols-1 gap-4">
                {/* Classified Items Section */}
                {filterData && (
                  <Card className="overflow-hidden border-0 bg-gradient-to-br from-white/95 to-gray-50/90 shadow-xl backdrop-blur-md dark:from-slate-900/95 dark:to-slate-800/90">
                    <div className="relative p-4">
                      <div className="absolute right-0 top-0 h-32 w-32 rounded-full bg-gradient-to-br from-blue-500/10 to-transparent blur-3xl" />
                      <div className="relative">
                        <ClassifiedItems items={filterData} />
                      </div>
                    </div>
                  </Card>
                )}

                {/* Data Table Section */}
                <Card className="overflow-hidden border-0 bg-gradient-to-br from-white/95 to-gray-50/90 shadow-xl backdrop-blur-md dark:from-slate-900/95 dark:to-slate-800/90">
                  <div className="relative">
                    <div className="absolute left-0 top-0 h-40 w-40 rounded-full bg-gradient-to-tr from-emerald-500/10 to-transparent blur-3xl" />
                    <ItemsTable items={filterData || undefined} />
                  </div>
                </Card>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!allData && !loading && (
            <Card className="border-0 bg-gradient-to-br from-white/95 to-gray-50/90 shadow-xl dark:from-slate-900/95 dark:to-slate-800/90">
              <InitialScreen />
            </Card>
          )}

          {/* Loading State */}
          {loading && (
            <Card className="border-0 bg-gradient-to-br from-white/95 to-gray-50/90 shadow-xl dark:from-slate-900/95 dark:to-slate-800/90">
              <div className="flex h-[75vh] flex-col items-center justify-center p-8">
                <LoadingBox />
              </div>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}

export default CompanyItemsReportPage;
